// default-prices.js — the built-in current-public-pricing seed map. The Pricing
// screen's "Seed defaults" button turns each row into an effective-dated
// ChargeItemDefinition (contract §3): url https://healther.io/ops/price/${priceKey},
// priceKey = `${provider}:${product}:${metric}`, a priceComponent amount, and the
// price-basis extension. EVERY row is marked `unverified: true` — the seeded entry
// shows an "unverified — confirm against your contracts" badge until the super-admin
// edits the rate (which creates a fresh effective-dated entry that is, by definition,
// confirmed). AWS is intentionally absent: AWS spend arrives as the `billed-cost`
// metric (money already), never priced from a rate.
//
// Prices researched from public pricing pages, July 2026. Treat as STARTING POINTS,
// not contract truth — real per-token/per-minute rates depend on your plan, region,
// and negotiated commitments. Sources are cited per row.

// ISO date every seeded price takes effect from. Chosen well before any plausible
// usage day so effective-price resolution (greatest effectiveFrom <= day) always hits.
export const SEED_EFFECTIVE_FROM = '2026-01-01';

export const DEFAULT_PRICES = [
  // ── OpenAI GPT-4o-mini — general extraction / summarisation LLM ──
  // https://developers.openai.com/api/docs/pricing  ($0.15 / $0.60 per 1M)
  { priceKey: 'openai:gpt-4o-mini:input-tokens', title: 'OpenAI GPT-4o-mini — input tokens', amount: 0.15, currency: 'USD', basis: 'per-1m' },
  { priceKey: 'openai:gpt-4o-mini:output-tokens', title: 'OpenAI GPT-4o-mini — output tokens', amount: 0.60, currency: 'USD', basis: 'per-1m' },

  // ── OpenAI GPT-5.4-mini-class — the consult-draft / conflict-review LLM ──
  // https://pricepertoken.com/pricing-page/model/openai-gpt-5-mini  (gpt-5-mini $0.25 / $2.00 per 1M — used as the 5.4-mini-class analog)
  { priceKey: 'openai:gpt-5-4-mini:input-tokens', title: 'OpenAI GPT-5.4-mini — input tokens', amount: 0.25, currency: 'USD', basis: 'per-1m' },
  { priceKey: 'openai:gpt-5-4-mini:output-tokens', title: 'OpenAI GPT-5.4-mini — output tokens', amount: 2.00, currency: 'USD', basis: 'per-1m' },

  // ── Cerebras — fast on-prem-adjacent inference (Gemma-4 31B) ──
  // https://www.cerebras.ai/pricing  (public listed range $0.10–$0.75 per 1M; gpt-oss-120b $0.35/$0.75 used as the analog — Llama/Gemma families are custom reserved-capacity)
  { priceKey: 'cerebras:gemma-4-31b:input-tokens', title: 'Cerebras Gemma-4 31B — input tokens', amount: 0.35, currency: 'USD', basis: 'per-1m' },
  { priceKey: 'cerebras:gemma-4-31b:output-tokens', title: 'Cerebras Gemma-4 31B — output tokens', amount: 0.75, currency: 'USD', basis: 'per-1m' },

  // ── Deepgram Nova-3 — streaming speech-to-text ──
  // https://deepgram.com/pricing  ($0.0077 / streaming minute, pay-as-you-go)
  { priceKey: 'deepgram:nova-3:audio-seconds', title: 'Deepgram Nova-3 — streaming STT', amount: 0.0077, currency: 'USD', basis: 'per-minute' },

  // ── Sarvam Saaras — Indic speech-to-text ──
  // https://www.sarvam.ai/api-pricing  (₹30 / audio hour = ₹0.50 / minute)
  { priceKey: 'sarvam:saaras:audio-seconds', title: 'Sarvam Saaras — Indic STT', amount: 0.50, currency: 'INR', basis: 'per-minute' },

  // ── Sarvam Bulbul — Indic text-to-speech ──
  // https://docs.sarvam.ai/api-reference-docs/models/bulbul  (₹30 / 10,000 chars = ₹3 / 1,000 chars)
  { priceKey: 'sarvam:bulbul:characters', title: 'Sarvam Bulbul — Indic TTS', amount: 3.0, currency: 'INR', basis: 'per-1k' },

  // ── ElevenLabs Conversational AI — voice agent minutes (LLM billed separately) ──
  // https://elevenlabs.io/blog/we-cut-our-pricing-for-conversational-ai  ($0.10 / minute, Creator/Pro)
  { priceKey: 'elevenlabs:convai:audio-seconds', title: 'ElevenLabs ConvAI — voice-agent minutes', amount: 0.10, currency: 'USD', basis: 'per-minute' },

  // ── Google Gemini 3.1 Flash Live — realtime bidi audio (billed per token) ──
  // https://ai.google.dev/gemini-api/docs/pricing  (Live API ~$1.00 / 1M input; output estimated)
  { priceKey: 'gemini:gemini-3-1-flash-live:input-tokens', title: 'Gemini 3.1 Flash Live — input tokens', amount: 1.00, currency: 'USD', basis: 'per-1m' },
  { priceKey: 'gemini:gemini-3-1-flash-live:output-tokens', title: 'Gemini 3.1 Flash Live — output tokens', amount: 2.00, currency: 'USD', basis: 'per-1m' },

  // ── OpenAI Realtime — realtime audio API (blended per-minute estimate) ──
  // https://developers.openai.com/api/docs/pricing  (realtime audio; ~$0.30 / minute blended estimate)
  { priceKey: 'openai-realtime:gpt-realtime:audio-seconds', title: 'OpenAI Realtime — audio minutes', amount: 0.30, currency: 'USD', basis: 'per-minute' },

  // ── Gupshup WhatsApp — per outbound message (Meta base + Gupshup markup, India) ──
  // https://codingclave.com/blog/gupshup-whatsapp-pricing-india-2026  (utility ~₹0.115 + ~₹0.085 markup ≈ ₹0.20 / message)
  { priceKey: 'gupshup:wa-message:messages', title: 'Gupshup WhatsApp — outbound message', amount: 0.20, currency: 'INR', basis: 'per-unit' },
];
